<script setup lang="ts">
import { CSSProperties, ref } from 'vue';
import { ColumnInterface, TableSortType } from './types';

const props = defineProps<{
	column: ColumnInterface;
	idx: number;
	sortBy: { column: string; direction: TableSortType };
}>();

const emit = defineEmits<{
	(e: 'clickHeader', column: ColumnInterface): void;
	(e: 'resizeColumn', $event: MouseEvent): void;
	(e: 'moveFrom', fromIndex: number): void;
	(e: 'moveTo', toIndex: number | null): void;
	(e: 'swapCols'): void;
}>();

const tableHeader = ref<HTMLTableCellElement | null>(null);
const isDragging = ref<boolean>(false);

const getSortArrowStyle = (column: ColumnInterface): CSSProperties => {
	if (props.sortBy.column === column.id) {
		return {
			transform: props.sortBy.direction === 'desc' ? 'rotate(0)' : 'rotate(180deg)',
			opacity: 1,
		};
	}
	return { transform: 'rotate(0)', opacity: 0.4 };
};

const onDrop = () => {
	emit('swapCols');
	isDragging.value = false;
	tableHeader.value?.classList.remove('table-header__over');
	return false;
};
</script>

<template>
	<th ref="tableHeader" scope="col" class="table-header">
		<div
			data-ts="drag-area"
			draggable="true"
			@dragstart="
				isDragging = true;
				$emit('moveFrom', props.idx);
			"
			@dragend="
				isDragging = false;
				$emit('moveTo', null);
			"
			@dragenter="$emit('moveTo', props.idx)"
			@dragover.prevent="() => false"
			@drop.stop="onDrop"
		>
			{{ column.label }}
			<button
				v-if="column.sortable"
				aria-label="Sort"
				type="button"
				tabindex="0"
				data-ts="sort-column-btn"
				class="table-header__sort-btn"
				@click="$emit('clickHeader', column)"
				@keypress.enter="$emit('clickHeader', column)"
			>
				<svg
					viewBox="0 0 24 24"
					width="12px"
					height="12px"
					fill="#656565"
					:style="getSortArrowStyle(column)"
				>
					<path
						d="M11.178 19.569a.998.998 0 0 0 1.644 0l9-13A.999.999 0 0 0 21 5H3a1.002 1.002 0 0 0-.822 1.569l9 13z"
					/>
				</svg>
			</button>
		</div>
		<div
			v-if="column.resizable && !isDragging"
			data-ts="resizer"
			class="resizer"
			@mousedown.stop="$emit('resizeColumn', $event)"
		/>
	</th>
</template>

<style lang="scss" scoped>
$bg-color: $color-neutral-100;
$dot-color: $color-black;
$dot-size: 1px;
$dot-space: 4px;

.table-header {
	cursor: move;

	&:active .resizer {
		opacity: 1;
		cursor: col-resize;
	}

	@media (hover: hover) {
		&:hover .resizer {
			opacity: 1;
			cursor: col-resize;
		}
	}

	&__sort-btn {
		width: 18px;
		margin-left: 4px;
		outline: none;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		vertical-align: middle;
		background-color: transparent;

		&:focus-visible {
			outline: 2px solid lightblue;
		}

		@media (hover: hover) {
			&:hover {
				outline: 2px solid lightblue;
			}
		}
	}
}

.resizer {
	position: absolute;
	top: 0;
	right: 0;
	width: 8px;
	user-select: none;
	height: 100%;
	background: linear-gradient(90deg, $bg-color ($dot-space - $dot-size), transparent 1%) center,
		linear-gradient($bg-color ($dot-space - $dot-size), transparent 1%) center, $dot-color;
	background-size: $dot-space $dot-space;
	opacity: 0;
}
</style>
